import { Injectable, inject } from '@angular/core';
import { Observable, catchError, forkJoin, map, of } from 'rxjs';
import { AlphaHttpClient } from './alpha-http-client';
import { IdsGroup } from './ids-adapter.service';
import { ProjectService, UserGroup } from './project.service';

@Injectable({ providedIn: 'root' })
export class AdminService {
    private httpClient = inject(AlphaHttpClient);
    private projectService = inject(ProjectService);

    // Domain URL for AlphaHttpClient
    private domainUrl = {
        type: 'environment',
        value: {
            __alpha_env__: 'ALPHA_UI_ADMIN_SERVICE_URL',
        },
    };

    /**
     * Fetch the reportees of a manager
     * @param username Manager's username
     * @returns Observable of GetReporteesResponse
     */
    getReportees(username: string): Observable<GetReporteesResponse> {
        return this.httpClient.post<GetReporteesResponse>(this.domainUrl, `/user/get-all-reportees`, { username });
    }

    /**
     * Fetch all users
     * @returns Observable of any (adjust interface if required)
     */
    fetchAllUsers(): Observable<any> {
        return this.httpClient.get<FetchAllUsersResponse>(this.domainUrl, `/user/fetch-all`);
    }

    /**
     * Delegate cases
     * @param delegateCaseData Array of delegate case data
     * @returns Observable of any (adjust interface if required)
     */
    delegateCases(delegateCaseData: Record<string, any>[]): Observable<any> {
        const project = this.projectService.fetchCurrentProject();
        return this.httpClient.post<any>(this.domainUrl, `/case-server/case/instance/delegate-all`, {
            projectId: project?.projectId,
            delegateCaseData,
        });
    }

    /**
     * Check if a user is a manager
     * @param username User's username
     * @returns Observable of IsManagerResponse
     */
    isManager(username: string): Observable<IsManagerResponse> {
        return this.httpClient.post<IsManagerResponse>(this.domainUrl, `/user/is-manager`, { username });
    }

    private shouldFetchAllUsers(projectUserGroups: UserGroup[]): boolean {
        // Fetch all users if no groups are found or Default Group is selected
        return (
            !projectUserGroups ||
            projectUserGroups.length === 0 ||
            projectUserGroups.some((group) => group.id === DEFAULT_GROUP.id)
        );
    }

    private mapUsers(response: FetchAllUsersResponse, fetchActiveUsers: boolean): GroupUser[] {
        // Transform and filter users based on fetchActiveUsers flag
        return (
            response.umas?.reduce((acc: GroupUser[], user: any) => {
                if (!fetchActiveUsers || (fetchActiveUsers && user.metadata?.isActive)) {
                    acc.push({
                        userName: user.user_id,
                        remainingBandwidth: user.metadata.remainingBandwidth || 0,
                        remainingDailyLimit: user.metadata.remainingDailyLimit || 0,
                        dailyLimit: user.metadata.dailyLimit || 0,
                        bandwidth: user.metadata.bandwidth || 0,
                    });
                }
                return acc;
            }, []) || []
        );
    }

    fetchUserByGroups(fetchActiveUsers: boolean): Observable<GroupUser[]> {
        const projectUserGroups = this.projectService.fetchCurrentProject()?.appConfig['userGroups'];

        if (this.shouldFetchAllUsers(projectUserGroups)) {
            return this.fetchAllUsers().pipe(
                map((response: FetchAllUsersResponse) => this.mapUsers(response, fetchActiveUsers)),
                catchError((error) => {
                    console.error('Error fetching all users:', error);
                    return of([]); // Return an empty observable in case of error
                }),
            );
        }

        // collect group_name as array of string
        const groupNames = projectUserGroups.map((group) => group.group_name);
        const groupUserObservables = groupNames.map((group) => this.fetchGroupUsers(group, fetchActiveUsers));

        // Combine all observables and flatten the results
        return forkJoin(groupUserObservables).pipe(
            map((results) => {
                const flatUsers = results.flat();
                const seen = new Set<string>();
                return flatUsers.filter((user) => {
                    const key = user.userName?.toLowerCase();
                    if (seen.has(key)) return false;
                    seen.add(key);
                    return true;
                });
            }),
            catchError((error) => {
                console.error('Error fetching users by groups:', error);
                return of([]);
            }),
        );
    }

    /**
     * Fetch users from a specific group with active status
     * @param group Group name
     * @param fetchActiveUsers Boolean to indicate if only active users should be fetched
     * @returns Observable of GroupUser[]
     */
    fetchGroupUsers(group: string, fetchActiveUsers: boolean): Observable<GroupUser[]> {
        return this.httpClient.post<GroupUser[]>(this.domainUrl, `/group/fetch-users`, {
            group,
            fetchActiveUsers,
        });
    }
}

// Interfaces for type safety
export interface Reportee {
    id: number;
    username: string;
    managerId: number;
}

export interface GetReporteesResponse {
    status: number;
    reportees: Reportee[];
}

export interface IsManagerResponse {
    status: number;
    isManager: boolean;
}

export interface GroupUser {
    userName: string;
    bandwidth: number;
    remainingBandwidth: number;
    dailyLimit: number;
    remainingDailyLimit: number;
}

export interface UserMetadata {
    remainingBandwidth: number;
    remainingDailyLimit: number;
    dailyLimit: number;
    bandwidth: number;
    isActive: boolean;
}

export interface User {
    id: number;
    user_id: string; // Assuming user_id is part of the response
    metadata: UserMetadata;
}

export interface FetchAllUsersResponse {
    umas: User[];
}

export const DEFAULT_GROUP: IdsGroup = { id: -1, group_name: 'Default', description: 'Default' };
